import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { hasOrgPermission } from '../permissions';
import type { CurrentOrganisation, CurrentUser } from '../user';
import { injectOrg, injectUser } from '../user';

export interface GuardCheckOptions {
    check: (user: CurrentUser) => boolean;
    redirectTo?: string[];
}

export interface GuardPermissionOptions {
    permissions: Parameters<typeof hasOrgPermission>[1];
    redirectTo?: string[];
}

export interface OrgGuardCheckOptions {
    check?: (org: CurrentOrganisation) => boolean;
    redirectTo?: string[];
}

export function createUserGuard(options: GuardCheckOptions) {
    const router = inject(Router);
    const user = injectUser()();
    const redirectPath = options.redirectTo ?? ['/'];

    // These guard factories are intended for routes already behind userLoadedGuard.
    if (!user) {
        return router.createUrlTree(['/']);
    }

    return options.check(user) ? true : router.createUrlTree(redirectPath);
}

export function createUserPermissionGuard(options: GuardPermissionOptions) {
    return createUserGuard({
        check: (user) => hasOrgPermission(user, options.permissions),
        redirectTo: options.redirectTo,
    });
}

export function createOrgGuard(options: OrgGuardCheckOptions = {}) {
    const router = inject(Router);
    const org = injectOrg()();
    const redirectPath = options.redirectTo ?? ['/'];

    // These guard factories are intended for routes already behind userLoadedGuard.
    if (!org) {
        return router.createUrlTree(['/']);
    }

    if (!options.check || options.check(org)) {
        return true;
    }

    return router.createUrlTree(redirectPath);
}
